import { FC, useState, useEffect, useMemo } from 'react';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import {
  AnalyzeItem,
  AnalyzeWallet,
  Button,
  HistoryEmpty,
  SvgIcon,
} from '@/components';
import {
  getSanitizedNumber,
  getAmountPrecision,
  getPrecisedNumber,
} from '@/helpers';
import { ChainsDictionary, RiskBadgeColors } from '@/config';
import { InvestigationHistoryItemData } from '@/redux/models';

import styles from './InvestigationHistoryRaw.module.sass';

interface InvestigationHistoryRawProps {
  item?: InvestigationHistoryItemData;
}

const InvestigationHistoryRaw: FC<InvestigationHistoryRawProps> = ({
  item,
}) => {
  const { t } = useTranslation();
  const [directIncomes, setDirectIncomes] = useState(
    item?.connections?.directIncomes || [],
  );
  const [indirectIncomes, setIndirectIncomes] = useState(
    item?.connections?.indirectIncomes || [],
  );
  const [directOutcomes, setDirectOutcomes] = useState(
    item?.connections?.directOutcomes || [],
  );
  const [indirectOutcomes, setIndirectOutcomes] = useState(
    item?.connections?.indirectOutcomes || [],
  );
  const [riskScoreClass, setRiskScoreClass] = useState(RiskBadgeColors.GREEN);
  const [totalIncome, setTotalIncome] = useState(0);
  const [totalOutcome, setTotalOutcome] = useState(0);

  useEffect(() => {
    if (!item) {
      return;
    }

    const incomed = [...directIncomes, ...indirectIncomes].reduce(
      (total: number, element: any) => {
        return total + getSanitizedNumber(element.value);
      },
      0,
    );
    const outcomed = [...directOutcomes, ...indirectOutcomes].reduce(
      (total: number, element: any) => {
        return total + getSanitizedNumber(element.value);
      },
      0,
    );

    setTotalIncome(incomed);
    setTotalOutcome(outcomed);

    if (item.riskscore === null) {
      return;
    }

    let cssClass = RiskBadgeColors.GREEN;
    if (item.riskscore >= 50) {
      cssClass = RiskBadgeColors.ORANGE;
    }
    if (item.riskscore >= 75) {
      cssClass = RiskBadgeColors.RED;
    }

    setRiskScoreClass(cssClass);
  }, [item]);

  const handleDownloadReport = () => {
    window.open(item?.xlsReport, '_blank');
  };

  const hasRoutingIncomes = !!indirectIncomes.length;
  const hasRoutingOutcomes = !!indirectOutcomes.length;

  const hasDirectIncomes = !!directIncomes.length;
  const hasDirectOutcomes = !!directOutcomes.length;

  const isTokenized =
    item?.tokenData &&
    Object.prototype.hasOwnProperty.call(item.tokenData, 'precision');
  const displayNetwork = ChainsDictionary[item?.asset || '']
    ? ChainsDictionary[item?.asset || ''].name
    : item?.network;

  const precisionLength = useMemo(() => {
    if (!item) {
      return 0;
    }

    return getAmountPrecision(item).toString().length;
  }, [item]);

  const displayIncome = useMemo(() => {
    if (!item) {
      return '';
    }

    return getPrecisedNumber(totalIncome, precisionLength);
  }, [item, precisionLength, totalIncome]);

  const displayOutcome = useMemo(() => {
    if (!item) {
      return '';
    }

    return getPrecisedNumber(totalOutcome, precisionLength);
  }, [item, precisionLength, totalOutcome]);

  return (
    <div className={cx(styles.investigations, styles.investigationsSingle)}>
      {!item?.connections && (
        <HistoryEmpty
          title={t('translation.investigation_block_title', {
            asset: displayNetwork,
          })}
          desc={t('translation.investigation_block_nodata_subtitle')}
        >
          <div>
            {!isTokenized && (
              <div>
                * {t('translation.investigation_block_main_coin_subtitle')}
              </div>
            )}
            {isTokenized && (
              <div>
                *{' '}
                {t('translation.investigation_block_token_subtitle', {
                  type: item?.tokenData?.type,
                  code: item?.tokenData?.code,
                  issuer: item?.tokenData?.issuer,
                })}
              </div>
            )}
          </div>
        </HistoryEmpty>
      )}
      {item?.connections && (
        <div>
          <div className={styles.head}>
            <div className={styles.title}>
              {t('translation.investigation_block_title', {
                asset: displayNetwork,
              })}
            </div>

            <div className={styles.legends}>
              <div
                className={`${styles.legend} ${
                  styles[`legend--${riskScoreClass}`]
                }`}
              >
                <div className={styles.legend__icon}>
                  <SvgIcon name="risk" />
                </div>
                <span className={styles.legend__text}>
                  {item?.riskscore === null ? (
                    <span>{t('translation.tpl_aml_no_score')}</span>
                  ) : (
                    <span>
                      {item?.riskscore}% {t('translation.tpl_aml_risk_score')}
                    </span>
                  )}
                </span>
              </div>
            </div>
            <Button type="secondary" size="s" onClick={handleDownloadReport}>
              <SvgIcon name="xls" />
              <span>Download XLS</span>
            </Button>
          </div>

          <div className={styles['title-note']}>
            {!isTokenized && (
              <div>
                {t('translation.investigation_block_main_coin_subtitle')}
              </div>
            )}
            {isTokenized && (
              <div>
                {t('translation.investigation_block_token_subtitle', {
                  type: item?.tokenData?.type,
                  code: item?.tokenData?.code,
                  issuer: item?.tokenData?.issuer,
                })}
              </div>
            )}
            <div>{t('translation.investigation_block_subtitle')}</div>
          </div>

          <div className={styles.body}>
            <div className={styles.straight}>
              <div
                className={cx(styles.items, styles['items--incoming'], {
                  [`${styles['is-empty']}`]: directIncomes.length === 0,
                  [`${styles['is-single']}`]: directIncomes.length === 1,
                  [`${styles['has-routing']}`]: hasRoutingIncomes,
                })}
              >
                <div className={styles.hat}>
                  <div className={styles.subtitle}>
                    {t('translation.inv_income_tx_title')} (
                    {directIncomes.length + indirectIncomes.length})
                  </div>
                  <div className={styles.stats}>
                    {t('translation.connections_received_title')}:{' '}
                    <b>
                      {displayIncome} {item?.displayAsset}
                    </b>
                  </div>
                </div>

                {!!directIncomes.length && (
                  <div className={styles.list}>
                    {directIncomes.map((connection: any) => (
                      <AnalyzeItem
                        key={connection.title || connection.address}
                        type="income"
                        title={connection.title || connection.address}
                        description={connection.description}
                        value={connection.value}
                        rawValue={connection.rawValue}
                        shareTotal={totalIncome}
                        caption={connection.caption}
                        clusterAddress={connection.clusterAddress}
                        isBlank={connection.isBlank}
                        isMuted={connection.isMuted}
                        className={styles.item__income}
                      />
                    ))}
                  </div>
                )}
              </div>

              <AnalyzeWallet
                address={item?.address}
                network={item?.network}
                incomeConnected={hasDirectIncomes || hasRoutingIncomes}
                outcomeConnected={hasDirectOutcomes || hasRoutingOutcomes}
                counterparty={item?.counterparty}
              />

              <div
                className={cx(styles.items, styles['items--outgoing'], {
                  [`${styles['is-empty']}`]: directOutcomes.length === 0,
                  [`${styles['is-single']}`]: directOutcomes.length === 1,
                  [`${styles['has-routing']}`]: hasRoutingOutcomes,
                })}
              >
                <div className={styles.hat}>
                  <div className={styles.subtitle}>
                    {t('translation.inv_outcome_tx_title')} (
                    {directOutcomes.length + indirectOutcomes.length})
                  </div>

                  <div className={styles.stats}>
                    {t('translation.connections_sent_title')}:{' '}
                    <b>
                      {displayOutcome} {item?.displayAsset}
                    </b>
                  </div>
                </div>

                {!!directOutcomes.length && (
                  <div className={styles.list}>
                    {directOutcomes.map((connection: any) => (
                      <AnalyzeItem
                        key={connection.title || connection.address}
                        type="outcome"
                        title={connection.title || connection.address}
                        description={connection.description}
                        value={connection.value}
                        rawValue={connection.rawValue}
                        shareTotal={totalOutcome}
                        caption={connection.caption}
                        clusterAddress={connection.clusterAddress}
                        isBlank={connection.isBlank}
                        isMuted={connection.isMuted}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>

            {(hasRoutingIncomes || hasRoutingOutcomes) && (
              <div className={styles.routing}>
                <div className={styles.routing__inner}>
                  <div className={styles.routing__title}>
                    <span>{t('translation.inv_with_route_str')}</span>
                  </div>

                  <div className={styles.routing__list}>
                    <div
                      className={cx(styles.items, styles['items--incoming'], {
                        [`${styles['is-single']}`]:
                          indirectIncomes.length === 1,
                        [`${styles['has-direct']}`]: hasDirectIncomes,
                      })}
                    >
                      {!!indirectIncomes.length && (
                        <div className={styles.list}>
                          {indirectIncomes.map((connection: any) => (
                            <AnalyzeItem
                              key={connection.title || connection.address}
                              type="income"
                              title={connection.title || connection.address}
                              description={connection.description}
                              value={connection.value}
                              rawValue={connection.rawValue}
                              shareTotal={totalIncome}
                              caption={connection.caption}
                              clusterAddress={connection.clusterAddress}
                              isBlank={connection.isBlank}
                              isMuted={connection.isMuted}
                              className={styles.item__income}
                            />
                          ))}
                        </div>
                      )}
                    </div>

                    <div className={styles.routingStretch} />

                    <div
                      className={cx(styles.items, styles['items--outgoing'], {
                        [`${styles['is-single']}`]:
                          indirectOutcomes.length === 1,
                        [`${styles['has-direct']}`]: hasDirectOutcomes,
                      })}
                    >
                      {!!indirectOutcomes.length && (
                        <div className={styles.list}>
                          {indirectOutcomes.map((connection: any) => (
                            <AnalyzeItem
                              key={connection.title || connection.address}
                              type="outcome"
                              title={connection.title || connection.address}
                              description={connection.description}
                              value={connection.value}
                              rawValue={connection.rawValue}
                              shareTotal={totalOutcome}
                              caption={connection.caption}
                              clusterAddress={connection.clusterAddress}
                              isBlank={connection.isBlank}
                              isMuted={connection.isMuted}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default InvestigationHistoryRaw;
